import { useTranslation } from 'react-i18next';
import { useAppState } from 'context/AppState';
import Msg from 'assets/icons/msg.svg';
import { isBrazil } from 'utils/countryCode';
import { isOsrSummarySupportedCrop } from 'pages/RecommendationV2/utils/isOsrSummarySupportedCrop';
import { trackSummaryFeedback } from 'utils/helpers/amplitude';
import { useReportOverviewData } from './useReportOverviewData';
import {
  StyledHeaderText,
  StyledHeaderSmallText,
  IconBox,
  StyledHeaderVerySmallText,
  StyledSummaryGenerationText,
  StyledAISummaryDeclarationText,
  StyledAISummaryWarningText,
  StyledFeedbackButton,
  StyledSummaryFeedbackContainer,
  StyledSummaryFeedbackText,
} from './ReportOverview.styles';
import thumbsup_icon from 'assets/icons/thumbsup_icon.svg';
import { useState, useEffect } from 'react';
import { Divider } from 'syngenta-digital-cropwise-react-ui-kit';
import { useBreakpoint } from 'hooks';
import { StyledSpinner } from '../RecommendationInfo/RecommendationInfo.styles';

interface ReportOverviewContentProps {
  showTitle?: boolean;
  renderHeaderSmall?: (content: React.ReactNode) => React.ReactNode;
  renderLabel?: (label: React.ReactNode) => React.ReactNode;
}

const ReportOverviewContent = ({
  showTitle = true,
  renderHeaderSmall,
  renderLabel,
}: ReportOverviewContentProps): JSX.Element => {
  const [t] = useTranslation();
  const {
    apiData: { recommendationCropwise, osrSummary },
  } = useAppState();

  const { isMobile } = useBreakpoint();

  const { reportOverviewDescription, cropName } = useReportOverviewData();
  const [isSummaryHelpful, setIsSummaryHelpful] = useState<boolean | undefined>(undefined);
  const [isInitiallySaved, setIsInitiallySaved] = useState(false);
  const [isDelayPassed, setIsDelayPassed] = useState(false);
  const reportId = recommendationCropwise?.id;
  const isDDG = recommendationCropwise?.source === 'DDG';

  // Check localStorage on component load to restore previous feedback
  useEffect(() => {
    if (reportId) {
      const feedbackKey = `ai_summary_feedback_${reportId}`;
      const storedFeedback = localStorage.getItem(feedbackKey);
      if (storedFeedback) {
        setIsInitiallySaved(true);
      }
    }
  }, [reportId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsDelayPassed(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleFeedbackClick = (helpful: boolean) => {
    setIsSummaryHelpful(helpful);

    if (reportId) {
      const feedbackKey = `ai_summary_feedback_${reportId}`;
      const feedbackData = {
        helpful,
        timestamp: Date.now(),
        email: recommendationCropwise?.email_id,
        country: recommendationCropwise?.country,
        crop: cropName,
      };
      localStorage.setItem(feedbackKey, JSON.stringify(feedbackData));
    }

    // Track with all data points
    trackSummaryFeedback(
      helpful,
      recommendationCropwise?.email_id,
      recommendationCropwise?.country,
      cropName
    );
  };

  const label =
    osrSummary?.status === 'COMPLETED' ? t('Cropwise AI Summary') : t('Report overview');
  const headerSmallContent = (
    <>
      <IconBox>
        <img src={Msg} alt="No img found" className="icon-size" />
      </IconBox>
      {renderLabel ? renderLabel(label) : <div style={{ paddingLeft: '8px' }}>{label}</div>}
    </>
  );

  return (
    <>
      {showTitle && (
        <StyledHeaderText data-testid="seed-info-crop-wise">
          {t('Our Expert Seed Recommendation')}
        </StyledHeaderText>
      )}
      <StyledHeaderSmallText>
        {renderHeaderSmall ? renderHeaderSmall(headerSmallContent) : headerSmallContent}
      </StyledHeaderSmallText>
      {osrSummary?.status !== 'COMPLETED' &&
        !isBrazil(recommendationCropwise?.country) &&
        isOsrSummarySupportedCrop(cropName) &&
        !isDDG && (
        <StyledSummaryGenerationText>
          <StyledSpinner size={18} />
          {t('Your AI Summary is being generated and will appear here shortly.')}
        </StyledSummaryGenerationText>
      )}
      {osrSummary?.status === 'COMPLETED' ? (
        <>
          <StyledHeaderVerySmallText>{osrSummary.summary}</StyledHeaderVerySmallText>
          <StyledAISummaryDeclarationText>
            {t('Summarized by Cropwise AI')}
            <StyledAISummaryWarningText data-testid="ai-summary-warning">
              {isMobile ? (
                <>
                  <span>{t('AI can sometimes make mistakes.')}</span>
                  <br />
                  <span>{t('Please review carefully.')}</span>
                </>
              ) : (
                t('AI can sometimes make mistakes. Please review carefully.')
              )}
            </StyledAISummaryWarningText>
          </StyledAISummaryDeclarationText>{' '}
          {!isInitiallySaved && isDelayPassed && (
            <>
              <Divider style={{ margin: '0px' }} />{' '}
              <StyledSummaryFeedbackContainer>
                {isSummaryHelpful === undefined ? (
                  <>
                    <StyledSummaryFeedbackText>
                      {t('Was this summary helpful?')}{' '}
                    </StyledSummaryFeedbackText>{' '}
                    <StyledFeedbackButton onClick={() => handleFeedbackClick(true)}>
                      <img src={thumbsup_icon} alt="thumbsup_icon" />{' '}
                    </StyledFeedbackButton>{' '}
                    <StyledFeedbackButton
                      onClick={() => handleFeedbackClick(false)}
                      style={{ rotate: '180deg' }}
                    >
                      <img src={thumbsup_icon} alt="thumbsdown_icon" />{' '}
                    </StyledFeedbackButton>{' '}
                  </>
                ) : (
                  <StyledSummaryFeedbackText>
                    {' '}
                    {t('Thanks for your feedback!')}{' '}
                  </StyledSummaryFeedbackText>
                )}
              </StyledSummaryFeedbackContainer>{' '}
            </>
          )}
        </>
      ) : (
        <StyledHeaderVerySmallText>{reportOverviewDescription}</StyledHeaderVerySmallText>
      )}
    </>
  );
};

export default ReportOverviewContent;
